import { FC, useMemo, useState, memo, useEffect, useCallback } from "react"
import { PaginationState, Row } from "@tanstack/react-table"
import { If, ListSkeleton, Table } from "../../../ui"
import { IntegrationCell, IntegrationCard } from "./cells"
import IntegrationCardSkeleton from "./components/IntegrationCardSkeleton"
import BrandAutocomplete from "../../brand-autocomplete"
import {
  Integration,
  BBColumnDef,
  Brand,
  IntegrationType
} from "../../../../types"
import { useInMobileView } from "../../../hooks"

interface TabDef {
  label: string
  type: IntegrationType | "all"
}

const TABS: TabDef[] = [
  { label: "All", type: "all" },
  { label: "Standalone", type: "standalone" },
  { label: "API", type: "api" },
  { label: "Loan Officer Page", type: "boomtown" },
  { label: "Website Chat", type: "chatbot" }
]

interface Props {
  todayIntegrations?: Integration[]
  /** Full (unsliced) history list — filtering and pagination happen client-side */
  integrations?: Integration[]
  brands?: Brand[]
  selectedBrandId: string
  onSelectedBrandChange: (brandId: string) => void
  isLoading: boolean
  onSelect?: (id: string) => void
}

const PAGE_SIZE = 10

const DateSectionHeader = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3 mb-2 mt-4">
    <span className="text-xs font-semibold uppercase tracking-widest text-[#757575] whitespace-nowrap">
      {label}
    </span>
    <div className="flex-1 h-px bg-[#e4e4e4]" />
  </div>
)

function matchesTab(
  integration: Integration,
  type: IntegrationType | "all"
): boolean {
  if (type === "all") return true
  return integration.type === type
}

function timestampToDate(created: string): Date {
  return new Date(parseInt(created, 10) * 1000)
}

function formatDateHeader(created: string): string {
  const date = timestampToDate(created)
  if (date.toDateString() === new Date().toDateString()) return "TODAY"
  return date
    .toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    })
    .toUpperCase()
}

function groupByDate(
  integrations: Integration[]
): { label: string; items: Integration[] }[] {
  const groups: Map<string, Integration[]> = new Map()

  for (const integration of integrations) {
    const dateKey = integration.created
      ? timestampToDate(integration.created).toDateString()
      : "Unknown"
    const items = groups.get(dateKey) ?? []
    items.push(integration)
    groups.set(dateKey, items)
  }

  return Array.from(groups.entries()).map(([, items]) => ({
    label: items[0].created
      ? formatDateHeader(items[0].created)
      : "UNKNOWN DATE",
    items
  }))
}

const IntegrationsTable: FC<Props> = memo(
  ({
    todayIntegrations = [],
    integrations = [],
    brands = [],
    selectedBrandId,
    onSelectedBrandChange,
    isLoading,
    onSelect
  }) => {
    const isMobile = useInMobileView()
    const listViewType = isMobile ? "grid" : "table"

    const [activeTab, setActiveTab] = useState<IntegrationType | "all">("all")
    const [pagination, setPagination] = useState<PaginationState>({
      pageIndex: 0,
      pageSize: PAGE_SIZE
    })

    // Reset to first page when brand changes
    useEffect(() => {
      setPagination(p => ({ ...p, pageIndex: 0 }))
    }, [selectedBrandId])

    const filteredToday = useMemo(
      () =>
        todayIntegrations.filter(integration =>
          matchesTab(integration, activeTab)
        ),
      [todayIntegrations, activeTab]
    )

    // Apply tab filter to full history list first, then paginate
    const filteredHistory = useMemo(
      () =>
        integrations.filter(integration => matchesTab(integration, activeTab)),
      [integrations, activeTab]
    )

    const totalCount = filteredHistory.length

    const pageCount = useMemo(
      () => Math.ceil(totalCount / pagination.pageSize) || 1,
      [totalCount, pagination.pageSize]
    )

    const pagedHistory = useMemo(() => {
      const start = pagination.pageIndex * pagination.pageSize
      return filteredHistory.slice(start, start + pagination.pageSize)
    }, [filteredHistory, pagination.pageIndex, pagination.pageSize])

    const historyGroups = useMemo(
      () => groupByDate(pagedHistory),
      [pagedHistory]
    )
    const hasNoResults =
      filteredToday.length === 0 && filteredHistory.length === 0

    const handleTabChange = (type: IntegrationType | "all") => {
      setActiveTab(type)
      setPagination(p => ({ ...p, pageIndex: 0 }))
    }

    const getTabButtonClassName = (isActive: boolean): string =>
      [
        "rounded-full px-4 py-1.5 text-sm border transition-colors",
        isActive
          ? "bg-[#f4effe] border-[#4a1a9d] text-[#4a1a9d] font-medium"
          : "bg-white border-[#e4e4e4] text-[#232227] font-normal"
      ].join(" ")

    const renderCard = useCallback(
      (row: Row<Integration>) => (
        <IntegrationCard data={row.original} onSelect={onSelect} />
      ),
      [onSelect]
    )

    const columns: BBColumnDef<Integration>[] = useMemo(
      () => [
        {
          id: "title",
          header: () => <>Name</>,
          footer: props => props.column.id,
          cell: info => (
            <IntegrationCell data={info.row.original} onSelect={onSelect} />
          )
        }
      ],
      [onSelect]
    )

    return (
      <div className="mt-6">
        <If
          condition={isLoading}
          then={
            isMobile ? (
              <div className="flex flex-col gap-3 py-4 mt-4 -mx-6 px-4 bg-primary-100">
                {[...Array(4)].map((_, i) => (
                  <IntegrationCardSkeleton key={i} />
                ))}
              </div>
            ) : (
              <ListSkeleton />
            )
          }
          else={
            <>
              {/* Toolbar */}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2 overflow-x-auto snap-x snap-mandatory scrollbar-none pb-0.5">
                  {TABS.map(tab => (
                    <button
                      key={tab.type}
                      type="button"
                      onClick={() => handleTabChange(tab.type)}
                      className={`snap-start shrink-0 ${getTabButtonClassName(
                        activeTab === tab.type
                      )}`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                {brands.length > 0 && (
                  <BrandAutocomplete
                    className="w-full max-w-none sm:max-w-md sm:w-auto"
                    hideLabel
                    brands={brands}
                    selected={selectedBrandId}
                    onChange={onSelectedBrandChange}
                  />
                )}
              </div>

              {/* Date-grouped rows */}
              <div
                className={
                  isMobile ? "mt-4 -mx-6 px-6 py-4 bg-primary-100" : "mt-8"
                }
              >
                {hasNoResults && (
                  <div className="flex min-h-[240px] items-center justify-center">
                    <p className="text-sm text-[#757575]">No results found.</p>
                  </div>
                )}

                {/* TODAY — always pinned at top */}
                {filteredToday.length > 0 && (
                  <div>
                    <DateSectionHeader label="TODAY" />
                    <Table
                      data={filteredToday}
                      columns={columns}
                      viewType={listViewType}
                      pageSize={Math.max(filteredToday.length, 1)}
                      pageCount={1}
                      searchBarVisible={false}
                      noHeader
                      removeMobilePagination
                      renderCard={renderCard}
                      gridClass="flex flex-col gap-3 py-2"
                      cardClassName="text-left bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                    />
                  </div>
                )}

                {/* Historical — client-paginated, grouped by date */}
                {historyGroups.map(group => (
                  <div key={group.label}>
                    <DateSectionHeader label={group.label} />
                    <Table
                      data={group.items}
                      columns={columns}
                      viewType={listViewType}
                      pageSize={Math.max(group.items.length, 1)}
                      pageCount={1}
                      searchBarVisible={false}
                      noHeader
                      removeMobilePagination
                      renderCard={renderCard}
                      gridClass="flex flex-col gap-3 py-2"
                      cardClassName="text-left bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                    />
                  </div>
                ))}

                {/* Pagination controls */}
                {pageCount > 1 && (
                  <Table
                    className="mt-4"
                    data={[]}
                    columns={columns}
                    viewType={listViewType}
                    pageSize={pagination.pageSize}
                    pageCount={pageCount}
                    pagination={pagination}
                    setPagination={setPagination}
                    rowCount={totalCount}
                    searchBarVisible={false}
                    noHeader
                    removeMobilePagination
                    noDataElement={<></>}
                  />
                )}
              </div>
            </>
          }
        />
      </div>
    )
  }
)

IntegrationsTable.displayName = "IntegrationsTable"

export default IntegrationsTable
